import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, RefreshCw, Mail, Phone, Copy, Check } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "converted", label: "Converted" },
  { key: "closed", label: "Closed" },
];

const STATUS_STYLES = {
  new: "bg-emerald/15 text-emerald border-emerald/30",
  contacted: "bg-amber/15 text-amber border-amber/30",
  converted: "bg-white/15 text-white border-white/25",
  closed: "bg-white/5 text-white/45 border-white/10",
};

export function AdminEnquiriesPanel() {
  const [filter, setFilter] = useState("new");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/lender-enquiries${f === "all" ? "" : `?status_filter=${f}`}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(filter); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/lender-enquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load(filter);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openItem = openId ? data.items.find((i) => i.id === openId) : null;

  return (
    <div className="space-y-6" data-testid="admin-enquiries-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-emerald/15 border border-emerald/30 flex items-center justify-center">
            <MessageCircle className="h-4 w-4 text-emerald" strokeWidth={2.6} />
          </div>
          <div>
            <div className="font-display font-extrabold text-[22px] text-white tracking-[-0.03em] leading-none">Panel Enquiries</div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-white/45 mt-1.5">
              Chat-first prospects · reply within 24h
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 p-1 bg-white/5 border border-white/10 rounded-full">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`enquiries-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-full text-[12px] font-bold transition-colors inline-flex items-center gap-1.5 ${
                  filter === f.key ? "bg-white text-navy" : "text-white/60 hover:text-white"
                }`}
              >
                {f.label}
                <span className={`text-[10px] font-mono font-extrabold px-1.5 py-0.5 rounded-full ${
                  filter === f.key ? "bg-navy/10 text-navy" : "bg-white/10 text-white/70"
                }`}>
                  {data.counts?.[f.key] ?? 0}
                </span>
              </button>
            ))}
          </div>
          <button
            onClick={() => load(filter)}
            data-testid="enquiries-refresh"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/15 bg-white/5 text-white/70 hover:text-white hover:bg-white/10"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={2.6} />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="rounded-[20px] border border-white/10 bg-white/[0.02] overflow-hidden">
        {loading && data.items.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-white/40 text-sm font-semibold">Loading enquiries…</div>
        ) : data.items.length === 0 ? (
          <div className="h-48 flex flex-col items-center justify-center gap-2">
            <MessageCircle className="h-8 w-8 text-white/20" strokeWidth={2.2} />
            <div className="text-white/40 text-sm font-semibold">No enquiries in this bucket yet.</div>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {data.items.map((e) => (
              <EnquiryRow key={e.id} enq={e} onOpen={() => setOpenId(e.id)} />
            ))}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {openItem && (
        <EnquiryDetail
          enq={openItem}
          onClose={() => setOpenId(null)}
          onStatus={(s) => updateStatus(openItem.id, s)}
        />
      )}
    </div>
  );
}

function EnquiryRow({ enq, onOpen }) {
  const created = new Date(enq.created_at);
  const dateStr = created.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  const timeStr = created.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return (
    <button
      onClick={onOpen}
      data-testid={`enquiry-row-${enq.id}`}
      className="w-full text-left px-6 py-5 hover:bg-white/[0.03] transition-colors flex items-center gap-4"
    >
      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
        <span className="font-display font-extrabold text-[13px] text-white">
          {(enq.company?.[0] || "?").toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="font-display font-extrabold text-[15.5px] text-white truncate">{enq.company}</div>
          <StatusPill status={enq.status} />
        </div>
        <div className="font-mono text-[11.5px] text-white/55 truncate">
          {enq.name} · {enq.email}{enq.ticket_size ? ` · ${enq.ticket_size}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-[11px] font-extrabold text-white/70">{dateStr}</div>
        <div className="font-mono text-[10px] text-white/40">{timeStr}</div>
      </div>
    </button>
  );
}

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.new;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-[0.12em] font-extrabold ${cls}`}>
      {status}
    </span>
  );
}

function EnquiryDetail({ enq, onClose, onStatus }) {
  const [copied, setCopied] = useState(null);
  const copy = (label, value) => {
    navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };
  const nextActions = [
    { key: "contacted", label: "Mark contacted", show: enq.status === "new" },
    { key: "converted", label: "Mark converted", show: enq.status !== "converted" && enq.status !== "closed" },
    { key: "closed", label: "Close", show: enq.status !== "closed" },
    { key: "new", label: "Reopen", show: enq.status === "closed" },
  ].filter((a) => a.show);
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="enquiry-detail">
      <div className="absolute inset-0 bg-navy/60 backdrop-blur-sm" onClick={onClose} />
      <div className="ml-auto w-full max-w-lg bg-navy border-l border-white/10 relative overflow-y-auto">
        <div className="p-7 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <StatusPill status={enq.status} />
            <button onClick={onClose} data-testid="enquiry-detail-close" className="text-white/60 hover:text-white text-[12px] font-bold">Close</button>
          </div>
          <div className="font-display font-extrabold text-[28px] text-white tracking-[-0.03em] leading-[1.1] mb-2">
            {enq.company}
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.14em] font-bold text-white/50 mb-8">
            Received {new Date(enq.created_at).toLocaleString("en-GB")}
          </div>

          <div className="space-y-3 mb-8">
            <CopyRow icon={Mail} label="Email" value={enq.email} onCopy={() => copy("email", enq.email)} copied={copied === "email"} />
            {enq.phone ? (
              <CopyRow icon={Phone} label="Phone" value={enq.phone} onCopy={() => copy("phone", enq.phone)} copied={copied === "phone"} />
            ) : null}
          </div>

          <div className="space-y-4 mb-8">
            <DetailField label="Contact name" value={enq.name} />
            <DetailField label="Products they fund" value={enq.products || "—"} />
            <DetailField label="Typical ticket size" value={enq.ticket_size || "—"} />
            <DetailField label="Best time to call" value={enq.best_time || "—"} />
            {enq.message ? (
              <div>
                <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 mb-2">Their note</div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-4 text-[13.5px] text-white/85 leading-[1.6] font-medium">
                  {enq.message}
                </div>
              </div>
            ) : null}
          </div>

          <div className="pt-6 border-t border-white/10 space-y-2.5">
            <a
              href={`mailto:${enq.email}?subject=Re: SwiftCash panel enquiry`}
              data-testid="enquiry-reply-email"
              className="w-full h-11 rounded-xl bg-emerald text-white font-extrabold text-[13.5px] hover:bg-emerald-2 shadow-emerald inline-flex items-center justify-center gap-2"
            >
              <Mail className="h-4 w-4" strokeWidth={2.4} /> Reply by email
            </a>
            <div className="grid grid-cols-2 gap-2">
              {nextActions.map((a) => (
                <button
                  key={a.key}
                  onClick={() => onStatus(a.key)}
                  data-testid={`enquiry-status-${a.key}`}
                  className="h-10 rounded-xl border border-white/15 bg-white/5 text-white/85 hover:bg-white/10 font-bold text-[12.5px]"
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 mb-1">{label}</div>
      <div className="text-[13.5px] text-white font-semibold">{value}</div>
    </div>
  );
}

function CopyRow({ icon: Icon, label, value, onCopy, copied }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3">
      <Icon className="h-4 w-4 text-white/50 shrink-0" strokeWidth={2.4} />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[9.5px] uppercase tracking-[0.12em] font-bold text-white/45">{label}</div>
        <div className="font-mono text-[13px] font-semibold text-white truncate">{value}</div>
      </div>
      <button onClick={onCopy} className="text-white/60 hover:text-white shrink-0" aria-label={`Copy ${label}`}>
        {copied ? <Check className="h-4 w-4 text-emerald" strokeWidth={2.6} /> : <Copy className="h-3.5 w-3.5" strokeWidth={2.4} />}
      </button>
    </div>
  );
}
